import React, { useEffect, useRef, useState } from 'react';

import auth from '../../services/auth';
import FirebasePasswordAuth from '../../services/firebasePasswordAuth';

const RESEND_COOLDOWN_SECONDS = 60;

const parseFirebaseErrorMessage = message => {
  if (typeof message !== 'string') return null;

  try {
    const parsed = JSON.parse(message);
    return parsed && parsed.error && parsed.error.message;
  } catch (err) {
    return message;
  }
};

const friendlyFirebaseError = error => {
  const messageCode = parseFirebaseErrorMessage(error && error.message);
  const nestedCode = error && error.error && error.error.message;
  const code = String((error && error.code) || nestedCode || messageCode || '')
    .replace(/^auth\//, '')
    .replace(/-/g, '_')
    .toUpperCase();
  const messages = {
    ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL: 'This email uses a different sign-in method. Try Google Sign-In.',
    API_KEY_INVALID: 'Email sign-in is temporarily unavailable. Please try Google Sign-In or contact support.',
    APP_NOT_AUTHORIZED: 'Email sign-in is temporarily unavailable. Please try Google Sign-In or contact support.',
    CONFIGURATION_NOT_FOUND: 'Email sign-in is not configured correctly. Please try Google Sign-In or contact support.',
    EMAIL_ALREADY_IN_USE: 'An account already exists for this email. Try signing in or resetting your password.',
    EMAIL_EXISTS: 'An account already exists for this email. Try signing in or resetting your password.',
    EMAIL_NOT_FOUND: 'The email or password is incorrect.',
    EXPIRED_ACTION_CODE: 'This Firebase link has expired. Request a new email and try again.',
    INVALID_ACTION_CODE: 'This Firebase link is invalid or has already been used.',
    INVALID_API_KEY: 'Email sign-in is temporarily unavailable. Please try Google Sign-In or contact support.',
    INVALID_CREDENTIAL: 'The email or password is incorrect.',
    INVALID_EMAIL: 'Enter a valid email address.',
    INVALID_LOGIN_CREDENTIALS: 'The email or password is incorrect. Check both fields and try again.',
    INVALID_PASSWORD: 'The email or password is incorrect.',
    MISSING_EMAIL: 'Enter your email address.',
    MISSING_PASSWORD: 'Enter your password.',
    NETWORK_REQUEST_FAILED: 'Unable to reach the sign-in service. Check your connection and try again.',
    OPERATION_NOT_ALLOWED: 'Email/password sign-in is currently unavailable. Please use Google Sign-In or contact support.',
    PASSWORD_DOES_NOT_MEET_REQUIREMENTS: 'This password does not meet the required password policy.',
    QUOTA_EXCEEDED: 'Email sign-in is temporarily busy. Please wait and try again.',
    REQUIRES_RECENT_LOGIN: 'For security, please sign in again before continuing.',
    TOO_MANY_ATTEMPTS_TRY_LATER: 'Too many attempts. Please wait a few minutes before trying again.',
    TOO_MANY_REQUESTS: 'Too many attempts. Please wait a few minutes before trying again.',
    USER_DISABLED: 'This account has been disabled. Contact support if you need help.',
    USER_NOT_FOUND: 'The email or password is incorrect.',
    USER_TOKEN_EXPIRED: 'Your sign-in session has expired. Please sign in again.',
    WEAK_PASSWORD: 'Choose a stronger password with at least six characters.'
  };

  if (messages[code]) return messages[code];

  const plainMessage = typeof messageCode === 'string' ? messageCode.trim() : '';
  const looksTechnical = !plainMessage
    || plainMessage.startsWith('{')
    || /^[A-Z0-9_]+$/.test(plainMessage)
    || /firebase|identity toolkit|\[auth\//i.test(plainMessage);

  return looksTechnical
    ? 'Unable to complete the request. Please check your details and try again.'
    : plainMessage;
};

function PasswordSignInDialog({ show, onClose, onSignedIn }) {
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [verificationCooldown, setVerificationCooldown] = useState(0);
  const [resetCooldown, setResetCooldown] = useState(0);
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  useEffect(() => {
    if (!verificationCooldown && !resetCooldown) return undefined;

    const timer = window.setTimeout(() => {
      setVerificationCooldown(value => Math.max(0, value - 1));
      setResetCooldown(value => Math.max(0, value - 1));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [verificationCooldown, resetCooldown]);

  if (!show) return null;

  const clearSensitiveFields = () => {
    if (passwordRef.current) passwordRef.current.value = '';
    if (confirmPasswordRef.current) confirmPasswordRef.current.value = '';
    setShowPassword(false);
  };

  const resetMessages = () => {
    setError('');
    setNotice('');
  };

  const closeDialog = () => {
    if (isLoading) return;
    clearSensitiveFields();
    resetMessages();
    setMode('signin');
    onClose();
  };

  const changeMode = nextMode => {
    clearSensitiveFields();
    resetMessages();
    setMode(nextMode);
  };

  const finishFirebaseSignIn = async () => {
    const idToken = await FirebasePasswordAuth.getIdToken(true);
    const user = await auth.signInWithFirebase(idToken);
    clearSensitiveFields();
    onSignedIn(user);
  };

  const sendResetEmail = async () => {
    if (resetCooldown > 0 || !email.trim()) return;
    setIsLoading(true);
    resetMessages();

    try {
      await FirebasePasswordAuth.sendPasswordResetEmail(email.trim());
      setResetCooldown(RESEND_COOLDOWN_SECONDS);
      setMode('reset-sent');
    } catch (err) {
      if (err && err.code === 'auth/user-not-found') {
        // Preserve the same response for existing and unknown email addresses.
        setResetCooldown(RESEND_COOLDOWN_SECONDS);
        setMode('reset-sent');
      } else {
        setError(friendlyFirebaseError(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const submit = async event => {
    event.preventDefault();
    resetMessages();
    const password = passwordRef.current ? passwordRef.current.value : '';
    const confirmPassword = confirmPasswordRef.current ? confirmPasswordRef.current.value : '';

    if (!email.trim()) {
      setError('Enter your email address.');
      return;
    }
    if (mode === 'reset') {
      await sendResetEmail();
      return;
    }
    if (!password) {
      setError('Enter your password.');
      return;
    }
    if (mode === 'create' && password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      if (mode === 'create') {
        await FirebasePasswordAuth.createAccount(email.trim(), password);
        await FirebasePasswordAuth.sendVerificationEmail();
        clearSensitiveFields();
        setVerificationCooldown(RESEND_COOLDOWN_SECONDS);
        setMode('verify');
        return;
      }

      const credential = await FirebasePasswordAuth.signIn(email.trim(), password);
      clearSensitiveFields();
      if (!credential.user.emailVerified) {
        setMode('verify');
        return;
      }

      await finishFirebaseSignIn();
    } catch (err) {
      setError(friendlyFirebaseError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const resendVerification = async () => {
    if (verificationCooldown > 0) return;
    setIsLoading(true);
    resetMessages();

    try {
      await FirebasePasswordAuth.reloadCurrentUser();
      await FirebasePasswordAuth.sendVerificationEmail();
      setVerificationCooldown(RESEND_COOLDOWN_SECONDS);
      setNotice('A new verification email has been sent.');
      setMode('verify');
    } catch (err) {
      setError(friendlyFirebaseError(err));
      setMode('verification-error');
    } finally {
      setIsLoading(false);
    }
  };

  const continueAfterVerification = async () => {
    setIsLoading(true);
    resetMessages();

    try {
      const firebaseUser = await FirebasePasswordAuth.reloadCurrentUser();
      if (!firebaseUser.emailVerified) {
        setError('Your email is not verified yet. Open the latest Firebase email, follow its link, then try again.');
        setMode('verify');
        return;
      }
      await finishFirebaseSignIn();
    } catch (err) {
      setError(friendlyFirebaseError(err));
      setMode('verification-error');
    } finally {
      setIsLoading(false);
    }
  };

  const useDifferentEmail = async () => {
    if (isLoading) return;
    try {
      await FirebasePasswordAuth.logOut();
    } catch (err) {
      // A missing/expired Firebase session should not prevent changing email.
    }
    setEmail('');
    setVerificationCooldown(0);
    changeMode('signin');
  };

  const titleByMode = {
    create: 'Create GoldAPI.io Account',
    reset: 'Reset Password',
    'reset-sent': 'Check Your Email',
    verify: 'Verify Your Email',
    'verification-error': 'Verification Failed',
    signin: 'GoldAPI.io - Sign In with Email'
  };
  const verificationMode = mode === 'verify' || mode === 'verification-error';
  const formMode = mode === 'signin' || mode === 'create' || mode === 'reset';

  return (
    <div className="modal d-block password-sign-in-modal" tabIndex="-1" role="dialog" aria-modal="true">
      <div className="modal-dialog modal-dialog-centered password-sign-in-dialog" role="document">
        <div className="modal-content password-sign-in-content">
          <form onSubmit={formMode ? submit : event => event.preventDefault()}>
            <div className="modal-header">
              <h5 className="modal-title">{titleByMode[mode]}</h5>
              <button type="button" className="close" aria-label="Close" disabled={isLoading} onClick={closeDialog}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              {verificationMode ? (
                <div className="password-auth-status">
                  <p>
                    {mode === 'verification-error'
                      ? 'Firebase could not confirm your verification status.'
                      : <>We sent a verification link to <strong>{email}</strong>. Open the link using Firebase’s hosted page, then return here.</>}
                  </p>
                  {error && <div className="alert alert-danger" role="alert">{error}</div>}
                  {notice && <div className="alert alert-success" role="status">{notice}</div>}
                  <button type="button" className="btn btn-warning btn-block" disabled={isLoading} onClick={continueAfterVerification}>
                    {isLoading ? 'Checking...' : 'I’ve Verified — Continue'}
                  </button>
                  <button type="button" className="btn btn-link btn-block" disabled={isLoading || verificationCooldown > 0} onClick={resendVerification}>
                    {verificationCooldown > 0 ? `Resend verification in ${verificationCooldown}s` : 'Resend Verification Email'}
                  </button>
                  <div className="password-auth-links password-auth-links-stacked">
                    <button type="button" disabled={isLoading} onClick={useDifferentEmail}>Use a Different Email</button>
                    <button type="button" disabled={isLoading} onClick={closeDialog}>Use Google Sign-In Instead</button>
                  </div>
                </div>
              ) : mode === 'reset-sent' ? (
                <div className="password-auth-status">
                  <p>Check your inbox for password-reset instructions.</p>
                  <p className="text-muted">If an account exists for <strong>{email}</strong>, Firebase will send a link to its hosted password-reset page.</p>
                  {error && <div className="alert alert-danger" role="alert">{error}</div>}
                  <button type="button" className="btn btn-link btn-block" disabled={isLoading || resetCooldown > 0} onClick={sendResetEmail}>
                    {resetCooldown > 0 ? `Send again in ${resetCooldown}s` : 'Send Reset Email Again'}
                  </button>
                  <div className="password-auth-links password-auth-links-stacked">
                    <button type="button" disabled={isLoading} onClick={() => changeMode('signin')}>Back to Sign In</button>
                    <button type="button" disabled={isLoading} onClick={closeDialog}>Use Google Sign-In Instead</button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-muted">
                    {mode === 'create'
                      ? 'Create an new GoldAPI.io account with your email and password. You will need to verify your email before signing in.'
                      : mode === 'reset'
                        ? 'Enter your email and GoldAPI.io will send password-reset instructions.'
                        : 'Use the email and password associated with your GoldAPI.io account or create a new GoldAPI.io account below.'}
                  </p>
                  <div className="form-group">
                    <label htmlFor="password-auth-email">Email</label>
                    <input id="password-auth-email" className="form-control" type="email" autoComplete="email" value={email} disabled={isLoading} onChange={event => setEmail(event.target.value)} />
                  </div>
                  {mode !== 'reset' && (
                    <div className="form-group">
                      <label htmlFor="password-auth-password">Password</label>
                      <div className="password-auth-input-row">
                        <input id="password-auth-password" className="form-control" type={showPassword ? 'text' : 'password'} autoComplete={mode === 'create' ? 'new-password' : 'current-password'} ref={passwordRef} disabled={isLoading} />
                        <button type="button" className="btn btn-outline-secondary password-auth-visibility" disabled={isLoading} onClick={() => setShowPassword(value => !value)}>{showPassword ? 'Hide' : 'Show'}</button>
                      </div>
                    </div>
                  )}
                  {mode === 'create' && (
                    <div className="form-group">
                      <label htmlFor="password-auth-confirm">Confirm password</label>
                      <input id="password-auth-confirm" className="form-control" type={showPassword ? 'text' : 'password'} autoComplete="new-password" ref={confirmPasswordRef} disabled={isLoading} />
                    </div>
                  )}
                  {error && <div className="alert alert-danger" role="alert">{error}</div>}
                  <div className="password-auth-links">
                    {mode !== 'signin' ? (
                      <button type="button" disabled={isLoading} onClick={() => changeMode('signin')}>Back to Sign In</button>
                    ) : (
                      <>
                        <button type="button" disabled={isLoading} onClick={() => changeMode('create')}>Create Account</button>
                        <button type="button" disabled={isLoading} onClick={() => changeMode('reset')}>Forgot Password?</button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            {formMode && (
              <div className="modal-footer password-auth-footer">
                <span className="password-auth-firebase-badge" aria-label="Authentication powered by Firebase">
                  <span className="password-auth-firebase-mark" aria-hidden="true">◆</span>
                  Powered by Firebase
                </span>
                <div className="password-auth-footer-actions">
                  <button type="button" className="btn btn-outline-secondary" disabled={isLoading} onClick={closeDialog}>Cancel</button>
                  <button type="submit" className="btn btn-warning" disabled={isLoading || (mode === 'reset' && resetCooldown > 0)}>
                    {isLoading ? 'Please wait...' : mode === 'create' ? 'Create Account' : mode === 'reset' ? 'Send Reset Email' : 'Sign In'}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

PasswordSignInDialog.defaultProps = {
  show: false,
  onClose: () => {},
  onSignedIn: () => {}
};

export default PasswordSignInDialog;
